// Incoming attacks across the player's cities (Ben, Oct 14 2026): the header's counter, the city switcher's red rows,
// its summary chips (land < 10 min · under attack · clear) and the "Incoming" sort (soonest landing first, grouped).
// The city list (target_char.villages) carries each city's num_incoming_attacks but no times; a city's soonest landing
// comes from its own village/get (queue_rally.ra_i), read while the switcher is open (state/incoming.ts).
import type { PlayerVillage, Village } from "@/api/types";
import { parseGameTime } from "./time";

/** An attack landing within this is urgent: the bright red row with the blinking dot. */
export const URGENT_MS = 10 * 60_000;
export const HOUR_MS = 60 * 60_000;

/** What a city's own village/get says: its soonest incoming attack's landing (ms, server clock), null when none is
 *  coming (the list's count can include supports), and when that was read. */
export interface CityIncoming {
  next: number | null;
  /** every incoming attack's landing, soonest first */
  lands: number[];
}
/** Keyed by city id; a city missing here hasn't been read yet. */
export type IncomingBook = Map<number, CityIncoming>;

/** The attacks heading for a city, from its village/get. */
export function incomingOf(v: Village | undefined, now: number): CityIncoming {
  const lands = (v?.queue_rally?.ra_i ?? [])
    .filter((m) => m.subtype === "a")
    .map((m) => parseGameTime(m.end_time))
    .filter((t) => Number.isFinite(t) && t > now - 1000)
    .sort((a, b) => a - b);
  return { next: lands[0] ?? null, lands };
}

/** A city's soonest attack still to land (ms), or null (none, or not read yet). */
export const nextOf = (book: IncomingBook, id: number, now: number): number | null =>
  book.get(id)?.lands.find((t) => t > now) ?? null;

export const incomingCount = (c: PlayerVillage) => Math.max(0, Number(c.num_incoming_attacks) || 0);

/** The total for the header's counter: every incoming attack across the cities. */
export const totalIncoming = (cities: PlayerVillage[]) => cities.reduce((n, c) => n + incomingCount(c), 0);

/** The header badge: 999 at most, then "999+". */
export const badgeCount = (n: number) => (n > 999 ? "999+" : String(n));

export type IncomingTier = "urgent" | "hour" | "later" | "unknown" | "clear";

/** Which band a city sits in: landing within 10 min, within the hour, later, under attack but not read yet (or the count
 *  is supports only), or nothing incoming. */
export function tierOf(c: PlayerVillage, book: IncomingBook, now: number): IncomingTier {
  if (!incomingCount(c)) return "clear";
  const next = nextOf(book, c.id, now);
  if (next === null) return "unknown";
  const left = next - now;
  return left < URGENT_MS ? "urgent" : left < HOUR_MS ? "hour" : "later";
}

export const TIER_LABEL: Record<IncomingTier, string> = {
  urgent: "Landing in 10 min",
  hour: "Within the hour",
  later: "Later",
  unknown: "Other incoming",
  clear: "No incoming",
};
const TIER_ORDER: IncomingTier[] = ["urgent", "hour", "later", "unknown", "clear"];

/** Soonest landing first; cities whose time isn't known yet after those (most attacks first); cities with nothing
 *  incoming last, in the order they came in (the player's chosen Sort). */
export function sortByIncoming(list: PlayerVillage[], book: IncomingBook, now: number): PlayerVillage[] {
  const rank = new Map(list.map((c, i) => [c.id, i]));
  const tier = new Map(list.map((c) => [c.id, TIER_ORDER.indexOf(tierOf(c, book, now))]));
  return [...list].sort((a, b) => {
    const ta = tier.get(a.id)!;
    const tb = tier.get(b.id)!;
    if (ta !== tb) return ta - tb;
    const na = nextOf(book, a.id, now);
    const nb = nextOf(book, b.id, now);
    if (na !== null && nb !== null && na !== nb) return na - nb;
    if (ta === TIER_ORDER.indexOf("unknown") && incomingCount(a) !== incomingCount(b)) return incomingCount(b) - incomingCount(a);
    return rank.get(a.id)! - rank.get(b.id)!;
  });
}

export interface IncomingGroup {
  tier: IncomingTier;
  cities: PlayerVillage[];
  attacks: number;
}

/** A list already sorted by incoming, cut into its bands (empty bands left out). */
export function incomingGroups(sorted: PlayerVillage[], book: IncomingBook, now: number): IncomingGroup[] {
  const out: IncomingGroup[] = [];
  for (const c of sorted) {
    const tier = tierOf(c, book, now);
    let g = out[out.length - 1];
    if (!g || g.tier !== tier) out.push((g = { tier, cities: [], attacks: 0 }));
    g.cities.push(c);
    g.attacks += incomingCount(c);
  }
  return out;
}

export interface IncomingSummary {
  attacks: number;
  /** cities with anything incoming */
  hit: number;
  /** cities with an attack landing within 10 min */
  urgent: number;
  clear: number;
  /** the soonest known landing across every city (ms), or null */
  next: number | null;
}

export function incomingSummary(cities: PlayerVillage[], book: IncomingBook, now: number): IncomingSummary {
  let attacks = 0, hit = 0, urgent = 0, next: number | null = null;
  for (const c of cities) {
    const n = incomingCount(c);
    if (!n) continue;
    attacks += n;
    hit++;
    if (tierOf(c, book, now) === "urgent") urgent++;
    const t = nextOf(book, c.id, now);
    if (t !== null && (next === null || t < next)) next = t;
  }
  return { attacks, hit, urgent, clear: cities.length - hit, next };
}

/** The summary chips double as filters: only the urgent cities, only those under attack, or only the clear ones. */
export type IncomingFilter = "urgent" | "hit" | "clear" | null;

export function filterIncoming(list: PlayerVillage[], book: IncomingBook, now: number, f: IncomingFilter): PlayerVillage[] {
  if (!f) return list;
  return list.filter((c) => {
    const t = tierOf(c, book, now);
    return f === "urgent" ? t === "urgent" : f === "hit" ? t !== "clear" : t === "clear";
  });
}

/** One world's incoming attacks (Oct 15 2026): every attack across its cities, and how many cities they hit. */
export interface WorldAttack {
  incoming: number;
  cities: number;
}
export const worldAttack = (cities: PlayerVillage[]): WorldAttack => ({
  incoming: totalIncoming(cities),
  cities: cities.filter((c) => incomingCount(c) > 0).length,
});

/** The World tab's alert: incoming attacks in every world but `current` (the world the player is in; null on the Portal). */
export const otherWorldsIncoming = (book: Map<number, WorldAttack>, current: number | null) =>
  [...book].reduce((n, [wid, a]) => (wid === current ? n : n + a.incoming), 0);
